/**
 * Onboarding Progress - Constellation-themed stepper
 *
 * Displays progress through the 6 modules as interconnected stars.
 * Shows current position, completed steps, and time remaining.
 */

import { useMemo } from 'react';
import {
  Sparkles,
  FileText,
  Shield,
  Cpu,
  LayoutDashboard,
  Award,
  Check,
  Lock,
} from 'lucide-react';
import type { ModuleId, OnboardingRole } from '../../types/onboarding';
import { getStepsForRole, getProgressPercentage } from '../../lib/onboarding/steps';
import { MODULE_TIME_ESTIMATES } from '../../lib/onboarding/constants';
import { cn } from '../../lib/utils';

const MODULE_ICONS = {
  welcome: Sparkles,
  legal: FileText,
  security: Shield,
  'system-education': Cpu,
  'dashboard-tour': LayoutDashboard,
  certification: Award,
} as const;

interface OnboardingProgressProps {
  role: OnboardingRole;
  currentModule: number;
  completedModules: ModuleId[];
  onModuleClick?: (index: number) => void;
  compact?: boolean;
}

export default function OnboardingProgress({
  role,
  currentModule,
  completedModules,
  onModuleClick,
  compact = false,
}: OnboardingProgressProps) {
  const steps = useMemo(() => getStepsForRole(role), [role]);
  const progressPercentage = useMemo(
    () => getProgressPercentage(completedModules, role),
    [completedModules, role]
  );

  // Calculate remaining time
  const remainingTime = useMemo(() => {
    let totalMin = 0;
    let totalMax = 0;

    steps.forEach((step, index) => {
      if (!completedModules.includes(step.id)) {
        const time = MODULE_TIME_ESTIMATES[step.id];
        totalMin += time.min;
        totalMax += time.max;
      }
    });

    return { min: totalMin, max: totalMax };
  }, [steps, completedModules]);

  // Get current step info
  const currentStep = steps[currentModule];

  if (compact) {
    return (
      <CompactProgress
        steps={steps}
        currentModule={currentModule}
        completedModules={completedModules}
        progressPercentage={progressPercentage}
        remainingTime={remainingTime}
      />
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span
            className="text-sm font-medium"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Step {currentModule + 1} of {steps.length}
          </span>
          <span
            className="text-sm"
            style={{ color: 'var(--color-text-muted)' }}
          >
            ~{remainingTime.min}-{remainingTime.max} min remaining
          </span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
        >
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              backgroundColor: 'var(--color-accent)',
              width: `${progressPercentage}%`,
            }}
          />
        </div>
      </div>

      {/* Constellation Stepper */}
      <div className="relative flex items-center justify-between px-4">
        {/* Connecting Lines */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 0 }}
        >
          {steps.slice(0, -1).map((_, index) => {
            const isCompleted =
              completedModules.includes(steps[index].id) &&
              completedModules.includes(steps[index + 1].id);
            const isActive =
              completedModules.includes(steps[index].id) ||
              index === currentModule;

            const x1 = `${(index / (steps.length - 1)) * 100}%`;
            const x2 = `${((index + 1) / (steps.length - 1)) * 100}%`;

            return (
              <line
                key={index}
                x1={x1}
                y1="50%"
                x2={x2}
                y2="50%"
                stroke={
                  isCompleted
                    ? 'var(--color-success)'
                    : isActive
                    ? 'var(--color-accent)'
                    : 'var(--color-border)'
                }
                strokeWidth="2"
                strokeDasharray={isCompleted ? '0' : '4 4'}
                className="transition-all duration-300"
              />
            );
          })}
        </svg>

        {/* Step Nodes */}
        {steps.map((step, index) => {
          const Icon = MODULE_ICONS[step.id];
          const isCompleted = completedModules.includes(step.id);
          const isCurrent = index === currentModule;
          const isLocked =
            !isCompleted &&
            !isCurrent &&
            index > completedModules.length;

          return (
            <button
              key={step.id}
              onClick={() => !isLocked && onModuleClick?.(index)}
              disabled={isLocked}
              className={cn(
                'relative z-10 flex flex-col items-center gap-2 group',
                'transition-all duration-200',
                isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              )}
              aria-label={`${step.title}${isCompleted ? ' (completed)' : isCurrent ? ' (current)' : ''}`}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {/* Star Node */}
              <div
                className={cn(
                  'w-12 h-12 rounded-full flex items-center justify-center',
                  'border-2 transition-all duration-300',
                  isCurrent && 'ring-4 ring-offset-2',
                  !isLocked && 'hover:scale-110'
                )}
                style={{
                  backgroundColor: isCompleted
                    ? 'var(--color-success)'
                    : isCurrent
                    ? 'var(--color-accent)'
                    : 'var(--color-bg-secondary)',
                  borderColor: isCompleted
                    ? 'var(--color-success)'
                    : isCurrent
                    ? 'var(--color-accent)'
                    : 'var(--color-border)',
                  boxShadow: isCurrent ? '0 0 0 4px var(--color-accent-muted)' : undefined,
                }}
              >
                {isCompleted ? (
                  <Check
                    className="w-5 h-5"
                    style={{ color: 'var(--color-bg)' }}
                  />
                ) : isLocked ? (
                  <Lock
                    className="w-4 h-4"
                    style={{ color: 'var(--color-text-muted)' }}
                  />
                ) : (
                  <Icon
                    className="w-5 h-5"
                    style={{
                      color: isCurrent
                        ? 'var(--color-bg)'
                        : 'var(--color-text-secondary)',
                    }}
                  />
                )}
              </div>

              {/* Label */}
              <span
                className={cn(
                  'text-xs font-medium text-center max-w-[80px] leading-tight',
                  'transition-colors duration-200'
                )}
                style={{
                  color: isCurrent
                    ? 'var(--color-accent)'
                    : isCompleted
                    ? 'var(--color-success)'
                    : 'var(--color-text-muted)',
                }}
              >
                {step.title}
              </span>

              {/* Hard/Soft Gate Indicator */}
              {step.hardGate && index === 2 && (
                <div
                  className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{
                    backgroundColor: 'var(--color-accent-muted)',
                    color: 'var(--color-accent)',
                  }}
                >
                  Dashboard unlocks here
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Current Step Info */}
      {currentStep && (
        <div
          className="mt-10 text-center p-4 rounded-xl"
          style={{ backgroundColor: 'var(--color-bg-secondary)' }}
        >
          <h3
            className="font-semibold mb-1"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {currentStep.title}
          </h3>
          <p
            className="text-sm"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {currentStep.description} (~{MODULE_TIME_ESTIMATES[currentStep.id].min}-
            {MODULE_TIME_ESTIMATES[currentStep.id].max} min)
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Compact Progress (for header display)
// ============================================================================

interface CompactProgressProps {
  steps: ReturnType<typeof getStepsForRole>;
  currentModule: number;
  completedModules: ModuleId[];
  progressPercentage: number;
  remainingTime: { min: number; max: number };
}

function CompactProgress({
  steps,
  currentModule,
  completedModules,
  progressPercentage,
  remainingTime,
}: CompactProgressProps) {
  const currentStep = steps[currentModule];

  return (
    <div className="flex items-center gap-4 py-2">
      {/* Mini Stars */}
      <div className="flex items-center gap-1">
        {steps.map((step, index) => {
          const isCompleted = completedModules.includes(step.id);
          const isCurrent = index === currentModule;

          return (
            <div
              key={step.id}
              className={cn(
                'w-2 h-2 rounded-full transition-all duration-200',
                isCurrent && 'w-3 h-3'
              )}
              style={{
                backgroundColor: isCompleted
                  ? 'var(--color-success)'
                  : isCurrent
                  ? 'var(--color-accent)'
                  : 'var(--color-border)',
              }}
            />
          );
        })}
      </div>

      {/* Progress Text */}
      <span
        className="text-sm"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {currentStep?.title}
      </span>

      {/* Time */}
      <span
        className="text-xs"
        style={{ color: 'var(--color-text-muted)' }}
      >
        ~{remainingTime.min} min left
      </span>
    </div>
  );
}
